import { useEffect, useState } from 'react';
import { Box, Button, TextField } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Header from 'components/Header/Header';
import { IHeader } from 'components/Header';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { IReducer } from 'Reducers/IReducer';
import { IRegisterProps } from './IRegister';
import { DesktopDatePicker, LocalizationProvider } from '@mui/lab';
import DateAdapter from '@mui/lab/AdapterMoment';
import moment from 'moment';
import { ICustomer, ITestGroup } from 'exactt-types/lib';
import { CustomFieldWithLabel } from 'components/CustomFieldWithLabel';
import AsyncSelect from 'react-select/async';
import { searchTestGroupList } from 'actions/LaboratoryActions';
import { searchCustomer } from 'actions/CustomerActions';

function _Register(props: IRegisterProps) {
  const navigate = useNavigate();
  const laboratoryId = props.laboratory?.details._id || '';
  const [startDate, setStartDate] = useState(moment().format('YYYY-MM-DD'));
  const [selectedCustomer, setSelectedCustomer] = useState<ICustomer | null>(null);
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
  const [selectedTestGroup, setSelectedTestGroup] = useState<ITestGroup | null>(null);
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');

  useEffect(() => {
    props.searchTestGroupList(laboratoryId, { page: 1, size: 100, query: '' });
  }, []);

  const breadcrumbsList: IHeader['breadcrumbsList'] = [
    {
      label: 'Dashboard',
      onClick: () => navigate('/'),
    },
    {
      label: 'Register',
      onClick: () => navigate('/laboratory/register'),
    },
  ];

  const testGroupOptions = async (inputValue: string) => {
    const list = await props.searchTestGroupList(laboratoryId, { page: 1, size: 20, query: inputValue });
    return list.data.testGroups.map((i: any) => ({
      value: i._id,
      label: i.name,
      option: i,
    }));
  };

  useEffect(() => {
    props.searchCustomer(laboratoryId, { page: 1, size: 20, query: customerSearchQuery });
    // eslint-disable-next-line
  }, [customerSearchQuery]);

  const promiseCustomerOptions = async (inputValue: string) => {
    const list = await props.searchCustomer(laboratoryId, { page: 1, size: 10, query: inputValue });

    return list.data.customers.map((i: any) => ({
      value: i._id,
      label: i.name,
      option: i,
    }));
  };

  const buildRegisterUrl = (path: string) => {
    const params = new URLSearchParams({
      from: startDate,
      to: endDate,
      startDate,
      endDate,
    });

    if (selectedTestGroup?._id) {
      params.set('testGroup', selectedTestGroup._id);
      params.set('groupName', selectedTestGroup.name || '');
    }

    if (selectedCustomer?._id) {
      params.set('customer', selectedCustomer._id);
    }

    return `${window.location.origin}${path}?${params.toString()}`;
  };

  return (
    <Box>
      <Header breadcrumbsList={breadcrumbsList} title="Register" />
      <Box
        pt={12}
        ml={3}
        display={'flex'}
        justifyContent={'center'}
        alignContent={'center'}
        flexDirection={'column'}
        width={400}
      >
        <Box display={'flex'} justifyContent={'center'} alignContent={'center'} gap={1}>
          <LocalizationProvider dateAdapter={DateAdapter}>
            <DesktopDatePicker
              label="Start Date"
              inputFormat="DD-MM-YYYY"
              value={moment(startDate, 'YYYY-MM-DD').toDate()}
              onChange={(data) => {
                setStartDate(moment(data).format('YYYY-MM-DD'));
              }}
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>

          <LocalizationProvider dateAdapter={DateAdapter}>
            <DesktopDatePicker
              label="End Date"
              inputFormat="DD-MM-YYYY"
              value={moment(endDate, 'YYYY-MM-DD').toDate()}
              onChange={(data) => {
                setEndDate(moment(data).format('YYYY-MM-DD'));
              }}
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
        </Box>

        <Box justifyContent={'center'} alignContent={'center'} gap={1} mt={1} mb={1}>
          <CustomFieldWithLabel width={120} label="Test Group">
            <AsyncSelect
              loadOptions={testGroupOptions}
              value={{ value: selectedTestGroup?._id, label: selectedTestGroup?.name }}
              defaultOptions={(props.testGroupsList || []).map((i) => ({
                label: i.name,
                value: i._id,
              }))}
              onChange={(newValue: any) => {
                const testGroup = props.testGroupsList?.find((i) => i._id === newValue?.value);
                if (testGroup) setSelectedTestGroup(testGroup);
              }}
            />
          </CustomFieldWithLabel>
        </Box>

        <Box justifyContent={'center'} alignContent={'center'} gap={1} mt={1} mb={1}>
          <CustomFieldWithLabel width={120} label="Customer">
            <AsyncSelect
              loadOptions={promiseCustomerOptions}
              value={{ value: selectedCustomer?._id, label: selectedCustomer?.name }}
              defaultOptions={props.searchCustomerList.map((i) => ({
                label: i.name,
                value: i._id,
              }))}
              onChange={(newValue: any) => {
                const findCustomer = props.searchCustomerList.find((i) => i._id === newValue?.value);
                if (findCustomer) setSelectedCustomer(findCustomer);
              }}
            />
          </CustomFieldWithLabel>
        </Box>

        <Button
          variant={'contained'}
          color={'primary'}
          sx={{ height: '40px', marginTop: '8px' }}
          onClick={() => {
            window.open(
              buildRegisterUrl(
                `/laboratory/print/${props.laboratory?.details?.type === 'PHARMA' ? 'inwardPharma' : 'inward'}`
              ),
              'Print',
              'width=1200,height=500'
            );
          }}
        >
          Inward / Outward
        </Button>
        <Button
          variant={'contained'}
          color={'primary'}
          onClick={() => {
            window.open(buildRegisterUrl('/laboratory/print/retained'), 'Print', 'width=1080,height=500');
          }}
          sx={{ height: '40px', marginTop: '8px' }}
        >
          Retained / Discarded
        </Button>
        <Button
          variant={'contained'}
          color={'primary'}
          onClick={() => {
            window.open(buildRegisterUrl('/laboratory/print/sampleregister'), 'Print', 'width=1080,height=500');
          }}
          sx={{ height: '40px', marginTop: '8px' }}
        >
          Sample Register
        </Button>
      </Box>
    </Box>
  );
}

const mapStateToProps = (state: IReducer) => {
  return {
    laboratory: state.laboratory.current,
    testGroupsList: state.laboratory.testGroups,
    searchCustomerList: state.laboratory.searchCustomer,
  };
};

function mapDispatchToProps(dispatch: any) {
  return {
    ...bindActionCreators(
      {
        searchTestGroupList,

        searchCustomer,
      },
      dispatch
    ),
  };
}

export const Register = connect(mapStateToProps, mapDispatchToProps)(_Register);
